import { useState, useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { AxiosError } from 'axios'
import { useTranslation } from 'react-i18next'

import { verify2FASchema, Verify2FAFormData } from '@/schemas/auth'
import { authService, ApiError } from '@/services/auth'
import { useAuthStore } from '@/stores/auth-store'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'

const DEVICE_TOKEN_KEY = 'archon_device_token'

function generateDeviceFingerprint(): string {
  const nav = window.navigator
  const screen = window.screen
  const fingerprint = [
    nav.userAgent,
    nav.language,
    screen.width,
    screen.height,
    screen.colorDepth,
    new Date().getTimezoneOffset(),
  ].join('|')
  return btoa(fingerprint).slice(0, 32)
}

export function Verify2FAPage() {
  const { t } = useTranslation('auth')
  const location = useLocation()
  const navigate = useNavigate()
  const setAuth = useAuthStore((state) => state.setAuth)

  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [temporaryToken, setTemporaryToken] = useState<string | null>(null)
  const [isResending, setIsResending] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)
  const [timeRemaining, setTimeRemaining] = useState(600)

  const codeInputRef = useRef<HTMLInputElement>(null)

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<Verify2FAFormData>({
    resolver: zodResolver(verify2FASchema),
    defaultValues: {
      rememberDevice: false,
    },
  })

  const rememberDevice = watch('rememberDevice')

  useEffect(() => {
    const state = location.state as { temporaryToken?: string } | null
    if (state?.temporaryToken) {
      setTemporaryToken(state.temporaryToken)
    } else {
      navigate('/login')
    }
  }, [location.state, navigate])

  useEffect(() => {
    if (timeRemaining <= 0) return

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timer)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [timeRemaining])

  useEffect(() => {
    if (resendCooldown <= 0) return

    const timer = setInterval(() => {
      setResendCooldown((prev) => Math.max(0, prev - 1))
    }, 1000)

    return () => clearInterval(timer)
  }, [resendCooldown])

  useEffect(() => {
    codeInputRef.current?.focus()
  }, [])

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const onSubmit = async (data: Verify2FAFormData) => {
    if (!temporaryToken) {
      setError(t('twoFactor.tokenNotFound'))
      return
    }

    setError(null)
    setIsLoading(true)

    try {
      const deviceFingerprint = generateDeviceFingerprint()
      const response = await authService.verify2FACode(
        data.code,
        temporaryToken,
        data.rememberDevice,
        deviceFingerprint
      )

      if (response.device_token) {
        localStorage.setItem(DEVICE_TOKEN_KEY, response.device_token)
      }

      if (response.user && response.access_token && response.refresh_token) {
        setAuth(response.user, response.access_token, response.refresh_token)
        navigate('/dashboard')
      }
    } catch (err) {
      const axiosError = err as AxiosError<ApiError>

      if (axiosError.response?.status === 401) {
        setError(t('twoFactor.invalidCode'))
      } else if (axiosError.response?.status === 400) {
        setError(axiosError.response.data?.detail || t('twoFactor.expiredCodeError'))
      } else if (axiosError.response?.status === 429) {
        setError(axiosError.response.data?.detail || t('twoFactor.tooManyAttempts'))
      } else {
        setError(t('twoFactor.errorVerifying'))
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendCode = async () => {
    if (!temporaryToken || resendCooldown > 0) return

    setIsResending(true)
    setError(null)

    try {
      const response = await authService.resend2FACode(temporaryToken)
      setTemporaryToken(response.temporary_token)
      setTimeRemaining(600)
      setResendCooldown(60)
    } catch (err) {
      const axiosError = err as AxiosError<ApiError>
      setError(
        axiosError.response?.data?.detail || t('twoFactor.errorResending')
      )
    } finally {
      setIsResending(false)
    }
  }

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6)
    setValue('code', value)
  }

  if (!temporaryToken) {
    return null
  }

  if (timeRemaining === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold">{t('twoFactor.codeExpired')}</CardTitle>
            <CardDescription>
              {t('twoFactor.codeExpiredMessage')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertDescription>
                {t('twoFactor.requestNewCode')}
              </AlertDescription>
            </Alert>
          </CardContent>
          <CardFooter className="flex flex-col gap-2">
            <Button
              variant="default"
              className="w-full"
              onClick={handleResendCode}
              disabled={isResending}
            >
              {isResending ? t('twoFactor.resending') : t('twoFactor.resendCodeButton')}
            </Button>
            <Link to="/login" className="w-full">
              <Button variant="outline" className="w-full">
                {t('twoFactor.backToLoginButton')}
              </Button>
            </Link>
          </CardFooter>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold">{t('twoFactor.title')}</CardTitle>
          <CardDescription>
            {t('twoFactor.subtitle')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="code">{t('twoFactor.code')}</Label>
              <Input
                id="code"
                type="text"
                inputMode="numeric"
                placeholder={t('twoFactor.codePlaceholder')}
                maxLength={6}
                {...register('code')}
                onChange={handleCodeChange}
                ref={codeInputRef}
                disabled={isLoading}
                className="text-center text-2xl tracking-widest font-mono"
              />
              {errors.code && (
                <p className="text-sm text-destructive">
                  {errors.code.message}
                </p>
              )}
              <p className="text-xs text-muted-foreground text-center">
                {t('twoFactor.expiresIn')} {formatTime(timeRemaining)}
              </p>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="rememberDevice"
                checked={rememberDevice}
                onCheckedChange={(checked: boolean) =>
                  setValue('rememberDevice', checked)
                }
                disabled={isLoading}
              />
              <Label htmlFor="rememberDevice" className="text-sm font-normal">
                {t('twoFactor.rememberDevice')}
              </Label>
            </div>

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? t('twoFactor.verifying') : t('twoFactor.verify')}
            </Button>
          </form>
        </CardContent>
        <CardFooter className="flex flex-col gap-2">
          <Button
            variant="ghost"
            className="w-full"
            onClick={handleResendCode}
            disabled={isResending || resendCooldown > 0}
          >
            {isResending
              ? t('twoFactor.resending')
              : resendCooldown > 0
              ? `${t('twoFactor.resendCodeIn')} ${resendCooldown}s`
              : t('twoFactor.resend')}
          </Button>
          <Link
            to="/login"
            className="text-sm text-muted-foreground hover:text-primary"
          >
            {t('twoFactor.backToLogin')}
          </Link>
        </CardFooter>
      </Card>
    </div>
  )
}
